// SIMETRIA în editor: gemenii unui element și imaginea lui prin oglindă sau
// translație — funcții pure pe geometrie, fără ecran.
//
// Serverul scrie pe geometrie orbitele de simetrie (`geometrie/orbite.py`,
// `G["simetrie"]`): OPERAȚIILE față de care se repetă arhitectura (o oglindă
// pe direcția X sau Y la `pozitie_mm`; o translație cu `pas_mm`) și ORBITELE:
// grupurile de elemente care sunt imaginea unul altuia, fiecare membru cu
// transformarea lui `τ = {ox, oy, celule}` față de ARHETIP (întâi oglinzile,
// apoi `celule` pași ai translației). Editorul nu hotărăște nimic despre
// simetrie: o citește și o ȚINE — când omul mută sau redimensionează un
// element, gemenii lui primesc aceeași schimbare, oglindită.
//
// Două reguli, după felul schimbării:
//   * POZIȚIA se duce ca DEPLASARE, nu ca imagine: geamănul se mută cu
//     oglindirea pasului făcut de element. Dacă nu stătea exact pe imaginea
//     lui (o excepție a arhitecturii), rămâne cu abaterea lui — editorul nu
//     «repară» pe tăcute ce n-a atins omul;
//   * SECȚIUNEA se duce ca IMAGINE: geamănul primește secțiunea elementului,
//     oglindită (`imagineaSectiunii`, aceeași regulă ca `orbite.imaginea`):
//     la oglinda PARALELĂ cu axa elementului alunecarea și brațele își
//     schimbă sensul în lung (`ofs_mm`, `la_mm`); la cea PERPENDICULARĂ
//     brațul trece de cealaltă parte (`ofs_mm` al brațului).
// Nu se poate mereu (geamănul lipsește de pe nivel, are altă axă): atunci
// geamănul rămâne cum e. Omul vede ce s-a dus prin conturul gemenilor pe plan.

import { type Esv, type Geometrie, type Pt, type SectiuneEsv } from "./editorGeometrie";

export type Operatie =
  | { id: string; fel: "oglinda"; directie: "X" | "Y"; pozitie_mm: number }
  | { id: string; fel: "translatie"; directie: "X" | "Y"; pas_mm: number };
export type Tau = { ox?: boolean; oy?: boolean; celule?: number };
export type Orbita = {
  id: string;
  operatii: string[];
  arhetip?: { id_arhetip: string };
  membri: { id: string; tau: Tau }[];
  /** membrii sunt exact imaginea arhetipului (poziție, axă, secțiune) */
  identica?: boolean;
};
export type Simetrie = { operatii?: Operatie[]; orbite?: Orbita[] } | null | undefined;

/** Un geamăn al elementului: cu `τ`-ul lui și al elementului, și operațiile
 *  orbitei, din care se compune drumul de la unul la altul. */
export type Geaman = { id: string; de: Tau; la: Tau; ops: Operatie[] };

/** Gemenii lui `id`: ceilalți membri ai orbitei lui; `[]` fără simetrie. */
export function geamanii(sim: Simetrie, id: string): Geaman[] {
  if (!sim?.orbite?.length) return [];
  const ops = new Map((sim.operatii ?? []).map((o) => [o.id, o]));
  for (const o of sim.orbite) {
    const eu = o.membri.find((m) => m.id === id);
    if (!eu) continue;
    const opsOrbita = o.operatii.map((k) => ops.get(k)).filter((x): x is Operatie => !!x);
    return o.membri.filter((m) => m.id !== id).map((m) => ({ id: m.id, de: eu.tau, la: m.tau, ops: opsOrbita }));
  }
  return [];
}

/** Drumul de la membrul `de` la membrul `la`: oglinzile care diferă între
 *  cei doi (oglinda e involuție, deci cele comune se anulează) și pașii
 *  translației. `p_la = T_la(T_de⁻¹(p_de))`, cu `T(p) = translație(oglinzi(p))`. */
export function drumul(g: Geaman): { ox: boolean; oy: boolean; ogl: { X?: number; Y?: number }; tr: { directie: "X" | "Y"; pas_mm: number } | null; deCelule: number; laCelule: number } {
  const ogl: { X?: number; Y?: number } = {};
  let tr: { directie: "X" | "Y"; pas_mm: number } | null = null;
  for (const o of g.ops) {
    if (o.fel === "oglinda") ogl[o.directie] = o.pozitie_mm;
    else tr = { directie: o.directie, pas_mm: o.pas_mm };
  }
  return {
    ox: !!g.de.ox !== !!g.la.ox && ogl.X != null,
    oy: !!g.de.oy !== !!g.la.oy && ogl.Y != null,
    ogl, tr, deCelule: g.de.celule ?? 0, laCelule: g.la.celule ?? 0,
  };
}

/** Punctul `p` al elementului, dus la geamăn. */
export function laGeaman(p: Pt, g: Geaman): Pt {
  const d = drumul(g);
  let [x, y] = p;
  // înapoi la arhetip: scade translația elementului, apoi oglinzile lui
  // (involuții: aceleași se aplică și la dus, deci se țin doar cele care diferă)
  if (d.tr) { if (d.tr.directie === "X") x -= d.deCelule * d.tr.pas_mm; else y -= d.deCelule * d.tr.pas_mm; }
  if (d.ox) x = 2 * d.ogl.X! - x;
  if (d.oy) y = 2 * d.ogl.Y! - y;
  if (d.tr) { if (d.tr.directie === "X") x += d.laCelule * d.tr.pas_mm; else y += d.laCelule * d.tr.pas_mm; }
  return [x, y];
}

/** Un VECTOR (o deplasare) dus la geamăn: doar oglinzile, fără translație. */
export function deplasareaLaGeaman(v: Pt, g: Geaman): Pt {
  const d = drumul(g);
  return [d.ox ? -v[0] : v[0], d.oy ? -v[1] : v[1]];
}

/** Secțiunea elementului cu axa `axa`, dusă la geamăn (regula lui
 *  `orbite.imaginea`): la oglinda paralelă cu axa, sensul în lung se
 *  întoarce (`ofs_mm`, `la_mm`); la cea perpendiculară, brațul trece de
 *  cealaltă parte (`ofs_mm` al brațului). Translația nu schimbă nimic. */
export function imagineaSectiunii(s: SectiuneEsv, axa: "X" | "Y", g: Geaman): SectiuneEsv {
  const d = drumul(g);
  const paralela = axa === "X" ? d.ox : d.oy;
  const perp = axa === "X" ? d.oy : d.ox;
  const out: SectiuneEsv = { ...s };
  if (paralela && out.ofs_mm) out.ofs_mm = -out.ofs_mm;
  if (s.brate?.length) {
    out.brate = s.brate.map((b) => ({
      ...b,
      la_mm: paralela && b.la_mm ? -b.la_mm : b.la_mm,
      ofs_mm: perp && b.ofs_mm ? -b.ofs_mm : b.ofs_mm,
    }));
  }
  return out;
}

/** Gemenii elementelor `ids` care nu sunt ei înșiși în `ids` (o schimbare
 *  pe clasă îi are deja pe cei mai mulți), fiecare o singură dată. */
export function gemeniiDeUrmat(sim: Simetrie, ids: readonly string[]): Map<string, { sursa: string; g: Geaman }> {
  const out = new Map<string, { sursa: string; g: Geaman }>();
  const in_ = new Set(ids);
  for (const id of ids)
    for (const g of geamanii(sim, id))
      if (!in_.has(g.id) && !out.has(g.id)) out.set(g.id, { sursa: id, g });
  return out;
}

/** Gemenii urmează ce s-a schimbat la `ids` între `G0` și `G1`:
 *  deplasarea nodului (oglindită) și secțiunea (ca imagine) pe nivelurile
 *  schimbate. Un geamăn cu altă axă decât sursa, sau care lipsește de pe
 *  nivelul schimbat, nu primește secțiunea; deplasarea o primește oricum. */
export function urmeazaSimetria(G0: Geometrie, G1: Geometrie, ids: readonly string[], sim: Simetrie): Geometrie {
  const gemeni = gemeniiDeUrmat(sim, ids);
  if (!gemeni.size) return G1;
  const dupa0 = new Map(G0.esv.map((e) => [e.id, e]));
  const dupa1 = new Map(G1.esv.map((e) => [e.id, e]));
  let schimbat = false;
  const esv = G1.esv.map((t) => {
    const u = gemeni.get(t.id);
    if (!u) return t;
    const e0 = dupa0.get(u.sursa);
    const e1 = dupa1.get(u.sursa);
    if (!e0 || !e1) return t;
    let nou: Esv = t;
    // rotirea: oglinda unei rotiri e tot o rotire
    if (e0.axa !== e1.axa) nou = { ...nou, axa: t.axa === "X" ? "Y" : "X" };
    // deplasarea
    const dx = e1.nod[0] - e0.nod[0], dy = e1.nod[1] - e0.nod[1];
    if (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6) {
      const v = deplasareaLaGeaman([dx, dy], u.g);
      nou = { ...nou, nod: [t.nod[0] + v[0], t.nod[1] + v[1]] };
    }
    // secțiunea, pe nivelurile pe care s-a schimbat și pe care geamănul le are
    if (nou.axa === e1.axa) {
      const spn = { ...nou.sectiune_per_nivel };
      let vreun = false;
      for (const [n, s1] of Object.entries(e1.sectiune_per_nivel)) {
        const s0 = e0.sectiune_per_nivel[n];
        if (!spn[n] || (s0 && JSON.stringify(s0) === JSON.stringify(s1))) continue;
        spn[n] = imagineaSectiunii(s1, e1.axa, u.g);
        vreun = true;
      }
      if (vreun) nou = { ...nou, sectiune_per_nivel: spn };
      // alunecarea elementului (nu a secțiunii)
      if ((e1.ofs_mm ?? 0) !== (e0.ofs_mm ?? 0)) {
        const d = drumul(u.g);
        const paralela = e1.axa === "X" ? d.ox : d.oy;
        nou = { ...nou, ofs_mm: paralela ? -(e1.ofs_mm ?? 0) : (e1.ofs_mm ?? 0) };
      }
    }
    if (nou !== t) schimbat = true;
    return nou;
  });
  return schimbat ? { ...G1, esv } : G1;
}

/** Un grup de simetrie din care face parte un ESV, cum îl arată panoul:
 *  ORBITA lui (`G.simetrie.orbite`), PERECHEA (oglinda față de axa dintre
 *  frontoane), GEAMĂNUL (celălalt capăt al liniei de zid, oglindit față de
 *  axa lungă) și CLASA DE OGLINDĂ — închiderea lor: bucata, geamănul de
 *  vizavi și perechile amândurora, până la patru membri. */
export type GrupSimetrie = {
  /** unic între grupurile unui element: felul și membrii */
  cheie: string;
  fel: "orbita" | "pereche" | "geaman" | "clasa";
  /** id-ul orbitei («O-ST5»); numai la `orbita` */
  orbita?: string;
  /** membrii, cu elementul cu tot, în ordinea naturală a id-urilor */
  membri: string[];
  /** operațiile orbitei; `[]` la celelalte */
  operatii: Operatie[];
  identica?: boolean;
};

const ordonat = (ids: Iterable<string>) =>
  [...new Set(ids)].sort((a, b) => a.localeCompare(b, "ro", { numeric: true }));

/** Grupurile de simetrie ale ESV-ului `id`, fără două cu aceiași membri (la
 *  egalitate rămâne cel mai bogat în sens: orbita, apoi clasa, perechea,
 *  geamănul). Legăturile `pereche` / `geaman` spre un element care nu mai e
 *  în geometrie se ignoră: grupul arată ce se poate selecta. */
export function grupurileLui(sim: Simetrie, esv: readonly Esv[], id: string): GrupSimetrie[] {
  const dupa = new Map(esv.map((e) => [e.id, e]));
  const eu = dupa.get(id);
  if (!eu) return [];
  const out: GrupSimetrie[] = [];
  const pune = (g: Omit<GrupSimetrie, "cheie">) => {
    if (g.membri.length < 2) return;
    const cheie = `${g.fel}:${g.membri.join("|")}`;
    if (out.some((x) => x.membri.join("|") === g.membri.join("|"))) return;
    out.push({ ...g, cheie });
  };
  const ops = new Map((sim?.operatii ?? []).map((o) => [o.id, o]));
  for (const o of sim?.orbite ?? []) {
    if (!o.membri.some((m) => m.id === id)) continue;
    pune({ fel: "orbita", orbita: o.id, identica: o.identica,
           membri: ordonat(o.membri.map((m) => m.id).filter((m) => dupa.has(m))),
           operatii: o.operatii.map((k) => ops.get(k)).filter((x): x is Operatie => !!x) });
  }
  const leg = (e: Esv | undefined, k: "pereche" | "geaman") => {
    const v = e?.[k];
    return v && dupa.has(v) ? v : null;
  };
  // clasa de oglindă: tot ce se atinge din element prin pereche și geamăn
  const clasa = new Set([id]);
  for (let coada = [id]; coada.length;) {
    const x = dupa.get(coada.pop()!);
    for (const k of ["pereche", "geaman"] as const) {
      const y = leg(x, k);
      if (y && !clasa.has(y)) { clasa.add(y); coada.push(y); }
    }
  }
  if (clasa.size > 2) pune({ fel: "clasa", membri: ordonat(clasa), operatii: [] });
  const p = leg(eu, "pereche");
  if (p) pune({ fel: "pereche", membri: ordonat([id, p]), operatii: [] });
  const g = leg(eu, "geaman");
  if (g) pune({ fel: "geaman", membri: ordonat([id, g]), operatii: [] });
  return out;
}
